import React, { useState, useRef, useEffect, useLayoutEffect, isValidElement, cloneElement, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { isCoarsePointer } from '../../utils/mobileSearchInput';

const MENU_MIN_WIDTH = 220;

function itemButtonClass(item, { sheet = false } = {}) {
  const sizeClass = sheet
    ? 'px-4 py-3.5 min-h-[48px] touch-manipulation text-base'
    : 'px-4 py-2 text-sm';

  if (item.disabled) {
    const mutedClass = item.description ? 'text-text-muted' : 'text-text-muted opacity-50';
    return `${sizeClass} w-full text-left cursor-not-allowed ${mutedClass}`;
  }

  const variantClass =
    item.variant === 'danger'
      ? 'text-danger hover:bg-danger-light'
      : item.variant === 'warning'
        ? 'text-warning hover:bg-warning-light'
        : item.variant === 'success'
          ? 'text-success-dark hover:bg-success-light'
          : 'text-text-primary hover:bg-surface-subtle';

  return `${sizeClass} w-full text-left transition-colors cursor-pointer ${variantClass}`;
}

/**
 * Dropdown menu component for action lists.
 * Each item may use `onClick` or `action` (either is invoked on select).
 *
 * - `usePortal` — fixed positioning via Floating UI so menus anchor to the trigger
 *   and aren't clipped by overflow containers.
 * - `useMobileSheet` — on touch devices, opens a bottom action sheet with large tap targets.
 */
const DropdownMenu = ({
  trigger,
  items = [],
  align = 'right',
  className = '',
  usePortal = false,
  useMobileSheet = false,
  sheetTitle = 'Choose an action',
  /** 'inline' shows helper text under disabled labels; 'tooltip' uses native title only (compact menus). */
  disabledHintMode = 'inline',
  open: openProp,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const isOpen = isControlled ? Boolean(openProp) : uncontrolledOpen;
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [coarsePointer] = useState(() => isCoarsePointer());
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const sheetMode = useMobileSheet && coarsePointer;

  const setOpen = useCallback(
    (next) => {
      const resolved = typeof next === 'function' ? next(isOpen) : next;
      if (isControlled) {
        onOpenChange?.(resolved);
      } else {
        setUncontrolledOpen(resolved);
      }
    },
    [isControlled, isOpen, onOpenChange]
  );

  useLayoutEffect(() => {
    if (!isOpen || !usePortal || sheetMode) return;

    let disposed = false;
    let stopAutoUpdate = () => {};

    const attach = () => {
      const reference = triggerRef.current;
      const floating = menuRef.current;
      if (!reference || !floating) return false;

      const placement = align === 'right' ? 'bottom-end' : 'bottom-start';

      const update = () => {
        computePosition(reference, floating, {
          strategy: 'fixed',
          placement,
          middleware: [
            offset(8),
            flip({
              padding: 8,
              fallbackPlacements: ['top-end', 'top-start', 'bottom-start', 'bottom-end'],
            }),
            shift({ padding: 8 }),
          ],
        }).then(({ x, y }) => {
          if (disposed) return;
          setMenuPosition({ top: y, left: x });
          floating.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        });
      };

      update();
      stopAutoUpdate = autoUpdate(reference, floating, update);
      return true;
    };

    if (!attach()) {
      const raf = requestAnimationFrame(() => attach());
      return () => {
        disposed = true;
        cancelAnimationFrame(raf);
        stopAutoUpdate();
      };
    }

    return () => {
      disposed = true;
      stopAutoUpdate();
    };
  }, [isOpen, usePortal, sheetMode, align, items.length]);

  useEffect(() => {
    if (!isOpen || !sheetMode) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, sheetMode]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const inTrigger = triggerRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    // Defer so the opening click doesn't immediately close the menu.
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleItemClick = (item) => {
    if (item.disabled) return;
    const run = item.onClick || item.action;
    if (typeof run === 'function') {
      run();
    }
    setOpen(false);
  };

  const renderItems = ({ sheet = false } = {}) =>
    items.map((item, index) => {
      if (item.divider) {
        return <div key={`divider-${index}`} className="my-1 border-t border-border-subtle" />;
      }

      const showInlineDisabledHint =
        item.disabled &&
        item.description &&
        (sheet || disabledHintMode === 'inline');

      return (
        <button
          key={item.label || index}
          type="button"
          role="menuitem"
          onClick={() => handleItemClick(item)}
          disabled={item.disabled}
          title={item.title || item.description || undefined}
          aria-label={
            item.disabled && (item.title || item.description)
              ? `${item.label}. ${item.title || item.description}`
              : undefined
          }
          className={itemButtonClass(item, { sheet })}
        >
          {showInlineDisabledHint ? (
            <div className="flex flex-col gap-1 text-left">
              <div className="flex items-center gap-2">
                {item.icon && <span className="text-base">{item.icon}</span>}
                <span>{item.label}</span>
              </div>
              <span className="text-xs font-normal leading-snug text-text-muted">{item.description}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {item.icon && <span className="text-base">{item.icon}</span>}
              <span>{item.label}</span>
            </div>
          )}
        </button>
      );
    });

  const floatingMenuContent = (
    <div
      ref={menuRef}
      role="menu"
      className={`
        rounded-lg border border-border-medium bg-surface shadow-lg
        ${usePortal ? 'fixed z-[2100]' : `absolute z-50 mt-2 ${align === 'right' ? 'right-0' : 'left-0'}`}
      `}
      style={
        usePortal
          ? { top: menuPosition.top, left: menuPosition.left, minWidth: MENU_MIN_WIDTH }
          : { minWidth: MENU_MIN_WIDTH }
      }
    >
      <div className="py-1">{renderItems({ sheet: false })}</div>
    </div>
  );

  const sheetContent = (
    <>
      <div
        className="fixed inset-0 z-[2090] bg-black/30"
        onClick={() => setOpen(false)}
        aria-hidden
      />
      <div
        ref={menuRef}
        role="menu"
        aria-label={sheetTitle}
        className="fixed bottom-0 left-0 right-0 z-[2100] rounded-t-xl border-t border-border-medium bg-surface shadow-xl pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      >
        <div className="flex justify-center pt-2 pb-1" aria-hidden>
          <div className="h-1 w-10 rounded-full bg-border-medium" />
        </div>
        {sheetTitle ? (
          <p className="px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            {sheetTitle}
          </p>
        ) : null}
        <div className="divide-y divide-border-subtle">{renderItems({ sheet: true })}</div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="mt-2 w-full border-t border-border-medium px-4 py-3.5 min-h-[48px] touch-manipulation text-base font-medium text-text-secondary hover:bg-surface-subtle"
        >
          Cancel
        </button>
      </div>
    </>
  );

  const setTriggerRef = (node) => {
    triggerRef.current = node;
  };

  const handleTriggerPointerDown = (event) => {
    event.stopPropagation();
  };

  const handleTriggerClick = (event) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const renderedTrigger = isValidElement(trigger)
    ? cloneElement(trigger, {
        ref: (node) => {
          setTriggerRef(node);
          const { ref } = trigger;
          if (typeof ref === 'function') ref(node);
          else if (ref && typeof ref === 'object') ref.current = node;
        },
        onPointerDown: (event) => {
          trigger.props.onPointerDown?.(event);
          handleTriggerPointerDown(event);
        },
        onClick: (event) => {
          trigger.props.onClick?.(event);
          handleTriggerClick(event);
        },
      })
    : (
        <button
          type="button"
          ref={setTriggerRef}
          onPointerDown={handleTriggerPointerDown}
          onClick={handleTriggerClick}
        >
          {trigger}
        </button>
      );

  return (
    <div className={`relative inline-flex ${className}`}>
      {renderedTrigger}

      {isOpen &&
        (sheetMode
          ? createPortal(sheetContent, document.body)
          : usePortal
            ? createPortal(floatingMenuContent, document.body)
            : floatingMenuContent)}
    </div>
  );
};

export default DropdownMenu;
